import { useCallback, useEffect, useRef, useState } from "react";
import { Bot, MessageSquare, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";
import { AI } from "@/constants/testIds";

function getSessionId() {
  let id = localStorage.getItem("rally_ai_session");
  if (!id) {
    id = `rally-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
    localStorage.setItem("rally_ai_session", id);
  }
  return id;
}

const SUGGESTIONS = [
  "What am I actually funding?",
  "What do I get as a Launch Wall backer?",
  "Can I pledge without paying?",
];

export default function AIChatBubble() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const r = await fetch(`${API}/ai/chat/${sessionId.current}`);
      const d = await r.json();
      if (Array.isArray(d.messages)) {
        setMsgs(d.messages.map((m) => ({ role: m.role, content: m.content })));
      }
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (open && msgs.length === 0) loadHistory();
  }, [open, msgs.length, loadHistory]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, streaming]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || streaming) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const resp = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: q }),
      });
      if (!resp.ok || !resp.body) throw new Error("stream failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let done = false;
      while (!done) {
        const chunk = await reader.read();
        done = chunk.done;
        if (chunk.value) {
          buf += decoder.decode(chunk.value, { stream: true });
          const parts = buf.split("\n\n");
          buf = parts.pop() || "";
          for (const p of parts) {
            const line = p.replace(/^data: /, "").trim();
            if (!line) continue;
            try {
              const evt = JSON.parse(line);
              if (evt.delta) {
                setMsgs((m) => {
                  const copy = [...m];
                  const last = copy[copy.length - 1];
                  if (last && last.role === "assistant") {
                    copy[copy.length - 1] = { ...last, content: last.content + evt.delta };
                  }
                  return copy;
                });
              } else if (evt.error) {
                setMsgs((m) => {
                  const copy = [...m];
                  copy[copy.length - 1] = { role: "assistant", content: `Error: ${evt.error}` };
                  return copy;
                });
              }
            } catch (e) { /* ignore */ }
          }
        }
      }
    } catch (e) {
      setMsgs((m) => {
        const copy = [...m];
        if (copy.length && copy[copy.length - 1].role === "assistant" && !copy[copy.length - 1].content) {
          copy[copy.length - 1] = { role: "assistant", content: "Couldn't reach the AI. Try again in a moment." };
        }
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const onSubmit = (e) => { e.preventDefault(); send(); };

  return (
    <>
      {!open && (
        <button
          data-testid={AI.bubbleOpen}
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-kinetic hover:bg-[#FF5E1A] text-black px-4 py-3 shadow-2xl transition-transform hover:-translate-y-0.5"
          aria-label="Open AI assistant"
        >
          <MessageSquare className="w-5 h-5" />
          <span className="font-bold uppercase text-sm tracking-wide">Ask Rally</span>
        </button>
      )}
      {open && (
        <div
          data-testid={AI.panel}
          className="fixed bottom-6 right-6 z-50 w-[min(380px,calc(100vw-2rem))] h-[min(560px,calc(100vh-6rem))] bg-slate-950 border border-white/15 shadow-2xl flex flex-col"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-kinetic flex items-center justify-center">
                <Bot className="w-4 h-4 text-black" />
              </div>
              <div>
                <div className="font-display font-bold text-sm uppercase tracking-tight">Rally Assistant</div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-slate-500">AI · GPT-6 Luna</div>
              </div>
            </div>
            <button
              data-testid={AI.close}
              onClick={() => setOpen(false)}
              className="text-slate-400 hover:text-white"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {msgs.length === 0 && (
              <div className="space-y-3">
                <div className="text-sm text-slate-300">
                  Hey — ask anything about Rally, the tiers, or how to back the fleet.
                </div>
                <div className="flex flex-col gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="text-left text-xs text-slate-300 px-3 py-2 border border-white/10 hover:border-kinetic hover:text-kinetic font-mono"
                    >
                      → {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-kinetic text-black"
                      : "bg-slate-900 border border-white/10 text-slate-200"
                  }`}
                >
                  {m.content || (streaming && i === msgs.length - 1 ? <span className="opacity-60">thinking…</span> : "")}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={onSubmit} className="border-t border-white/10 p-3 flex items-center gap-2">
            <input
              data-testid={AI.input}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about Rally..."
              className="flex-1 bg-slate-900 border border-white/15 px-3 py-2 text-sm outline-none focus:border-kinetic"
              disabled={streaming}
            />
            <Button
              data-testid={AI.send}
              type="submit"
              disabled={streaming || !input.trim()}
              className="rounded-none bg-kinetic hover:bg-[#FF5E1A] text-black px-3 h-9"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
